import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { showAppError } from '@/lib/errors';
import { safeToast } from '@/lib/errors/governedToast';

export interface TalentSearchFilters {
  keyword: string;
  skills: string[];
  title: string;
  location: string;
  company: string;
  company_tenure: string;
  min_experience?: number;
  max_experience?: number;
  education_level: string;
  languages: string[];
  availability: string;
  work_modes: string[];
  page: number;
  page_size: number;
}

export interface TalentCandidate {
  candidate_id: string;
  display_name: string;
  email: string | null;
  phone: string | null;
  phone_visibility: 'visible' | 'hidden_by_candidate' | 'not_available';
  visibility_mode: string;
  headline: string | null;
  current_job_title: string | null;
  professional_summary: string | null;
  total_years_experience: number | null;
  highest_education_level: string | null;
  country: string | null;
  city: string | null;
  availability_status: string | null;
  preferred_work_modes: string[];
  preferred_industries: string[];
  avatar_url: string | null;
  profile_completion_score: number | null;
  salary_expectation_min: number | null;
  salary_expectation_max: number | null;
  salary_currency: string | null;
  share_cv_download: boolean;
  allow_invites: boolean;
  skills: { skill_name: string; proficiency_level: string | null }[];
  languages: { language_name: string; proficiency_level: string | null }[];
  certifications: { certification_name: string; issuing_organization: string | null; issue_date: string | null }[];
  already_invited?: boolean;
  invite_status?: string | null;
  invited_job_ids?: string[];
  invite_job_statuses?: Record<string, { status: string; attempts: number; last_at: string }>;
  has_general_invite?: boolean;
}

const DEFAULT_FILTERS: TalentSearchFilters = {
  keyword: '',
  skills: [],
  title: '',
  location: '',
  company: '',
  company_tenure: '',
  education_level: '',
  languages: [],
  availability: '',
  work_modes: [],
  page: 1,
  page_size: 20,
};

export function useTalentSearch() {
  const qc = useQueryClient();
  const { user, profile, loading: authLoading } = useAuth();
  const [filters, setFilters] = useState<TalentSearchFilters>(DEFAULT_FILTERS);
  const [searchTriggered, setSearchTriggered] = useState(true);

  const searchQuery = useQuery({
    queryKey: ['talent-search', filters],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase.functions.invoke('talent-search', {
        body: filters,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      return data as { candidates: TalentCandidate[]; total: number; page: number; page_size: number };
    },
    enabled: searchTriggered && !!user?.id && !!profile?.org_id && !authLoading,
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const inviteMutation = useMutation({
    mutationFn: async (params: {
      candidate_id: string;
      target_hire_job_id?: string;
      invite_message?: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      const inviteAttemptId = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke('send-invite', {
        body: {
          ...params,
          idempotency_key: inviteAttemptId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      safeToast.success('Invite sent successfully');
      qc.invalidateQueries({ queryKey: ['talent-search'] });
      qc.invalidateQueries({ queryKey: ['employer-invites'] });
      qc.invalidateQueries({ queryKey: ['talent-discovery-meter'] });
    },
    onError: (err: Error) => {
      showAppError(err, { product: 'forkhire', domain: 'talent_discovery' });
    },
  });

  const search = (newFilters?: Partial<TalentSearchFilters>) => {
    if (newFilters) {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    setSearchTriggered(true);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchTriggered(false);
  };

  return {
    filters,
    setFilters,
    candidates: searchQuery.data?.candidates ?? [],
    total: searchQuery.data?.total ?? 0,
    isLoading: searchQuery.isLoading,
    isFetching: searchQuery.isFetching,
    search,
    resetFilters,
    sendInvite: inviteMutation.mutate,
    isSendingInvite: inviteMutation.isPending,
  };
}
